import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";

const STATUS_COLORS = {
  Normal: "#22c55e",
  Warning: "#f59e0b",
  Critical: "#ef4444",
  OFF: "#94a3b8",
};

// Health status tooltips (based on % of rated power) — easy wording
const HEALTH_TOOLTIPS = {
  Normal: "Machine is running fine. No issues.",
  Warning: "Machine is close to its rated power. Keep an eye on it.",
  Critical: "Machine has crossed its rated power. Needs attention now.",
};

// Light background tints so the whole box reflects the health status
const STATUS_BG = {
  Normal: "#f0fdf4",
  Warning: "#fffbeb",
  Critical: "#fef2f2",
  OFF: "#f8fafc",
};


const CATEGORY_BADGE = {
  GRID: { label: "Main", bg: "#dbeafe", fg: "#1e40af" },
  SOLAR: { label: "Solar", bg: "#d1fae5", fg: "#065f46" },
  GENERATOR: { label: "Generator", bg: "#fef3c7", fg: "#92400e" },
  VIRTUAL: { label: "Virtual", bg: "#f1f5f9", fg: "#475569" },
  MACHINE: { label: "Machine", bg: "#f1f5f9", fg: "#475569" },
  LOAD: { label: "Load", bg: "#f1f5f9", fg: "#475569" },
};

const isSourceCat = (cat) =>
  ["GRID", "SOLAR", "GENERATOR"].includes((cat || "").toUpperCase());

const HierarchyTreeView = ({
  hierarchyTree,
  latestData,
  healthStatusData,
  loading,
  error,
  unitPrice = 0,
}) => {
  // Fully responsive viewport fill.
  // Measures container's offset from top via ref + ResizeObserver,
  // CSS calc() acts as fallback before first measurement.
  const containerRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);

  useEffect(() => {
    const recompute = () => {
      if (!containerRef.current) return;
      const top = containerRef.current.getBoundingClientRect().top;
      const h = window.innerHeight - top - 12;
      if (h > 100) setAvailableHeight(h);
    };
    recompute();
    const raf = requestAnimationFrame(recompute);
    const t1 = setTimeout(recompute, 100);
    const t2 = setTimeout(recompute, 300);
    const t3 = setTimeout(recompute, 800);
    window.addEventListener("resize", recompute);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(recompute);
      observer.observe(document.body);
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener("resize", recompute);
      if (observer) observer.disconnect();
    };
  }, []);

  // ===== meter index map =====
  const meterIndexMap = useMemo(() => {
    if (!hierarchyTree) return new Map();
    const totals = {};
    const countAll = (node) => {
      if (node.meter_id)
        totals[node.meter_id] = (totals[node.meter_id] || 0) + 1;
      (node.children || []).forEach(countAll);
    };
    countAll(hierarchyTree);
    const seen = {};
    const indexMap = new Map();
    const assign = (node) => {
      const mid = node.meter_id;
      if (mid && totals[mid] > 1) {
        seen[mid] = (seen[mid] || 0) + 1;
        indexMap.set(node.machine_name, seen[mid]);
      }
      (node.children || []).forEach(assign);
    };
    assign(hierarchyTree);
    return indexMap;
  }, [hierarchyTree]);

  // ===== readings =====
  const readingsMap = useMemo(() => {
    const map = new Map();
    if (!latestData?.readings) return map;
    latestData.readings.forEach((r) => {
      const mid = r.meter_id;
      if (!mid) return;
      const key = r.meter_index != null ? `${mid}_${r.meter_index}` : mid;
      map.set(key, r);
    });
    return map;
  }, [latestData]);

  const getReading = (node) => {
    if (!node?.meter_id) return null;
    const idx = meterIndexMap.get(node.machine_name);
    const key = idx != null ? `${node.meter_id}_${idx}` : node.meter_id;
    return readingsMap.get(key) || null;
  };

  const lookupHealth = (mid, name) => {
    if (!healthStatusData || !mid) return null;
    const idx = meterIndexMap.get(name);
    let raw = null;
    if (idx != null && healthStatusData[`${mid}_${idx}`]) {
      raw = healthStatusData[`${mid}_${idx}`];
    } else if (healthStatusData[mid]) {
      raw = healthStatusData[mid];
    } else {
      const keys = Object.keys(healthStatusData);
      const match = keys.find((k) => k === mid || k.startsWith(`${mid}_`));
      raw = match ? healthStatusData[match] : null;
    }
    // Backend shape: object { percent, status }. "-" / anything else → no status.
    if (raw && typeof raw === "object") {
      return { status: raw.status || null, percent: Number(raw.percent) };
    }
    return null;
  };

  // ===== Decorate full tree =====
  const decoratedTree = useMemo(() => {
    if (!hierarchyTree) return null;

    const decorate = (node, path = "", isRoot = false) => {
      // Use whatever category backend sends. If none, no badge.
      const cat = (node.category || "").toUpperCase();
      const reading = getReading(node);
      const directPower = reading?.power_kw ?? null;
      const nodePath = `${path}/${node.machine_name || "?"}`;
      const children = (node.children || []).map((c) => decorate(c, nodePath));
      const isVirtual = !!node.is_virtual || cat === "VIRTUAL";

      let power;
      // Root node: use backend's totalUsage for consistency with the header
      if (isRoot && latestData?.totalUsage != null) {
        power = latestData.totalUsage;
      } else if (directPower != null) power = directPower;
      else if (isVirtual)
        power = children
          .filter((c) => !isSourceCat(c.category) || c.category === "GRID")
          .reduce((s, c) => s + (c.power || 0), 0);
      else power = 0;

      const isStale = !!reading?.is_stale;
      const threshold = cat === "GENERATOR" ? 0.5 : 0.05;
      const isOff = !isVirtual && Math.abs(power) <= threshold;
      const isExporting = cat === "GRID" && power < -threshold;

      let healthInfo = null;
      if (!isVirtual && !isOff)
        healthInfo = lookupHealth(node.meter_id, node.machine_name);
      const healthStatus = healthInfo?.status || null;
      const healthPercent = healthInfo?.percent ?? null;

      const statusKey = isOff
        ? "OFF"
        : healthStatus === "Warning"
          ? "Warning"
          : healthStatus === "Critical"
            ? "Critical"
            : healthStatus === "Normal"
              ? "Normal"
              : null;

      // Recursive count of non-virtual descendants and their on/off split
      let total = 0;
      let online = 0;
      const walk = (n) => {
        if (!n.is_virtual) {
          total += 1;
          if (!n.is_off) online += 1;
        }
        n.children.forEach(walk);
      };
      children.forEach(walk);
      const counts = { total, online, offline: total - online };

      return {
        id: nodePath,
        name: node.machine_name || "Unnamed",
        category: cat,
        is_virtual: isVirtual,
        meter_id: node.meter_id || null,
        power,
        is_stale: isStale,
        is_exporting: isExporting,
        is_off: isOff,
        status_key: statusKey,
        health_status: healthStatus,
        health_percent: healthPercent,
        reading,
        children,
        counts,
      };
    };
    return decorate(hierarchyTree, "", true);
  }, [hierarchyTree, readingsMap, meterIndexMap, healthStatusData, latestData]);

  // ===== Status counts (recursive) =====
  const statusCounts = useMemo(() => {
    const counts = { Normal: 0, Warning: 0, Critical: 0, Unknown: 0, OFF: 0 };
    if (!decoratedTree) return counts;
    const walk = (n) => {
      if (!n.is_virtual) {
        if (n.is_off) counts.OFF += 1;
        else if (n.status_key === "Normal") counts.Normal += 1;
        else if (n.status_key === "Warning") counts.Warning += 1;
        else if (n.status_key === "Critical") counts.Critical += 1;
        else counts.Unknown += 1;
      }
      n.children.forEach(walk);
    };
    decoratedTree.children.forEach(walk);
    return counts;
  }, [decoratedTree]);

  const totalPower = latestData?.totalUsage ?? decoratedTree?.power ?? 0;

  // ===== Expanded set (default: root + first level) =====
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (!decoratedTree) return;
    const init = new Set();
    init.add(decoratedTree.id);
    decoratedTree.children.forEach((c) => init.add(c.id));
    setExpanded(init);
  }, [decoratedTree?.id]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => {
    if (!decoratedTree) return;
    const all = new Set();
    const walk = (n) => {
      all.add(n.id);
      n.children.forEach(walk);
    };
    walk(decoratedTree);
    setExpanded(all);
  };

  const collapseAll = () => {
    if (!decoratedTree) return;
    setExpanded(new Set([decoratedTree.id]));
  };

  // ===== Details modal state =====
  const [selectedItem, setSelectedItem] = useState(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  // Close modal on outside click
  useEffect(() => {
    if (!selectedItem) return;
    const close = () => setSelectedItem(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [selectedItem]);

  const openDetails = (e, node) => {
    if (node.is_virtual) return; // virtual nodes have no details
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const panelW = 300;
    const panelH = 560;
    let left = rect.right + 10;
    let top = rect.top;
    if (left + panelW > window.innerWidth) left = rect.left - panelW - 10;
    if (left < 10) left = 10;
    if (top + panelH > window.innerHeight) top = window.innerHeight - panelH - 10;
    if (top < 10) top = 10;
    setPopupPos({ top, left });
    setSelectedItem(node);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>Loading hierarchy…</div>
      </div>
    );
  }

  if (error || !decoratedTree) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.empty, color: "#ef4444" }}>
          {error || "No data available"}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        ...styles.container,
        height: availableHeight ?? styles.container.height,
      }}
    >
      {/* ===== Summary Header ===== */}
      <div style={styles.header}>
        <div style={styles.headerLeftGroup}>
          <div style={styles.headerLeft}>
            <div style={styles.headerLabel}>Total Load</div>
            <div style={styles.headerValue}>
              {totalPower.toFixed(2)} <span style={styles.headerUnit}>kW</span>
            </div>
          </div>

          <div style={styles.headerCost}>
            <div style={styles.headerLabel}>Operating Cost / hr</div>
            <div style={styles.headerCostValue}>
              <span style={styles.headerCostCurrency}>PKR</span>{" "}
              {unitPrice > 0 && totalPower > 0
                ? (totalPower * unitPrice).toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })
                : "--"}
            </div>
          </div>
        </div>
        <div style={styles.headerCounts}>
          <CountChip color={STATUS_COLORS.Normal} label="Normal" count={statusCounts.Normal} />
          <CountChip color={STATUS_COLORS.Warning} label="Warning" count={statusCounts.Warning} />
          <CountChip color={STATUS_COLORS.Critical} label="Critical" count={statusCounts.Critical} />
          <CountChip
            color="#0ea5e9"
            label="On"
            count={
              statusCounts.Normal +
              statusCounts.Warning +
              statusCounts.Critical +
              statusCounts.Unknown
            }
          />
          <CountChip color={STATUS_COLORS.OFF} label="Off" count={statusCounts.OFF} />
        </div>
        <div style={styles.headerActions}>
          <button style={styles.actionBtn} onClick={expandAll}>
            Expand all
          </button>
          <button style={styles.actionBtn} onClick={collapseAll}>
            Collapse all
          </button>
        </div>
      </div>

      {/* ===== Org Chart ===== */}
      <ZoomablePane expanded={expanded}>
        <OrgNode
          node={decoratedTree}
          depth={0}
          expanded={expanded}
          onToggle={toggle}
          onBoxClick={openDetails}
        />
      </ZoomablePane>

      {/* ===== Details Panel ===== */}
      {selectedItem && (
        <DetailsPanel
          node={selectedItem}
          pos={popupPos}
          totalPower={totalPower}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};

// ===== Zoomable / pannable pane =====
const ZoomablePane = ({ children, expanded }) => {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [autoFit, setAutoFit] = useState(true);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const dragRef = useRef(null);

  // Measure inner's natural unscaled size
  const measureInner = useCallback(() => {
    const inner = innerRef.current;
    if (!inner) return { w: 0, h: 0 };
    return { w: inner.scrollWidth, h: inner.scrollHeight };
  }, []);

  // Update natural size after content changes
  useEffect(() => {
    const t = setTimeout(() => {
      const sz = measureInner();
      if (sz.w > 0 && sz.h > 0) setNaturalSize(sz);
    }, 60);
    return () => clearTimeout(t);
  }, [expanded, measureInner]);

  // Compute fit-to-screen zoom — uses BOTH width and height of the chart area
  const fitToScreen = useCallback(() => {
    if (!wrapRef.current || !innerRef.current) return;
    const cw = wrapRef.current.clientWidth - 24;
    const ch = wrapRef.current.clientHeight - 24;
    const sz = measureInner();
    if (sz.w <= 0 || sz.h <= 0) return;
    setNaturalSize(sz);
    const fitW = cw / sz.w;
    const fitH = ch / sz.h;
    // Use the smaller fit so both dimensions fit — fills as much as possible
    const next = Math.min(fitW, fitH);
    setZoom(Math.max(0.3, Math.min(2.5, next)));
    setPan({ x: 0, y: 0 });
  }, [measureInner]);

  // Auto-fit when expanded set changes
  useEffect(() => {
    if (!autoFit) return;
    const t = setTimeout(fitToScreen, 50);
    return () => clearTimeout(t);
  }, [expanded, autoFit, fitToScreen]);

  // Auto-fit when window resizes
  useEffect(() => {
    if (!autoFit) return;
    const onResize = () => fitToScreen();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [autoFit, fitToScreen]);

  const zoomIn = () => {
    setAutoFit(false);
    setZoom((z) => Math.min(2, z + 0.1));
  };
  const zoomOut = () => {
    setAutoFit(false);
    setZoom((z) => Math.max(0.3, z - 0.1));
  };
  const handleFit = () => {
    setAutoFit(true);
    fitToScreen();
  };
  // Kept for the (currently hidden) 1:1 button — re-enable when needed.
  // eslint-disable-next-line no-unused-vars
  const reset100 = () => {
    setAutoFit(false);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Drag-to-pan
  const onMouseDown = (e) => {
    if (e.target.closest("[data-no-pan]")) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pan.x,
      origY: pan.y,
    };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
  };
  const onMouseUp = () => {
    dragRef.current = null;
  };

  // Ctrl+wheel / pinch zoom — must be native non-passive listener
  // so preventDefault actually stops the browser's page zoom.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setAutoFit(false);
      const delta = e.deltaY < 0 ? 0.05 : -0.05;
      setZoom((z) => Math.max(0.3, Math.min(2, z + delta)));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  return (
    <div style={styles.zoomOuter}>
      {/* Zoom controls */}
      <div style={styles.zoomBar} data-no-pan>
        <button style={styles.zoomBtn} onClick={zoomOut} title="Zoom out">
          −
        </button>
        <span style={styles.zoomPct}>{Math.round(zoom * 100)}%</span>
        <button style={styles.zoomBtn} onClick={zoomIn} title="Zoom in">
          +
        </button>
        <button
          style={{ ...styles.zoomBtn, ...styles.zoomBtnText }}
          onClick={handleFit}
          title="Fit to screen"
        >
          Fit
        </button>
        {/* 1:1 button hidden for now — re-enable in future if needed.
            Resets zoom to actual size (100%) + pan to top-left, no scaling.
        <button
          style={{ ...styles.zoomBtn, ...styles.zoomBtnText }}
          onClick={reset100}
          title="Actual size (100%) — shows boxes at their true pixel size, no scaling"
        >
          1:1
        </button>
        */}
      </div>

      <div
        ref={wrapRef}
        style={styles.chartWrap}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {/* Sized wrapper takes the SCALED dimensions so DOM flow is correct */}
        <div
          style={{
            width: naturalSize.w > 0 ? naturalSize.w * zoom : "auto",
            height: naturalSize.h > 0 ? naturalSize.h * zoom : "auto",
            position: "relative",
            margin: "0 auto",
            transition: dragRef.current ? "none" : "width 0.15s, height 0.15s",
          }}
        >
          <div
            ref={innerRef}
            style={{
              position: naturalSize.w > 0 ? "absolute" : "static",
              top: 0,
              left: 0,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "top left",
              transition: dragRef.current ? "none" : "transform 0.15s",
              display: "inline-block",
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Count chip =====
const CountChip = ({ color, label, count }) => (
  <div style={styles.countChip}>
    <span style={{ ...styles.countDot, background: color }} />
    <span style={styles.countLabel}>{label}</span>
    <strong style={{ ...styles.countNum, color }}>{count}</strong>
  </div>
);

// ===== Recursive org chart node =====
// Renders: [Box] -> if expanded and has children -> | (down line) -> ┬─── (h line) -> [Children with stubs]
const OrgNode = ({ node, depth, expanded, onToggle, onBoxClick }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const isRoot = depth === 0;
  const childCount = hasChildren ? node.children.length : 0;

  // Measure first/last child centers so h-line spans actual positions even
  // when child subtrees have very different widths.
  const childrenRowRef = useRef(null);
  const childRefs = useRef([]);
  const [hLine, setHLine] = useState({ left: 0, right: 0 });

  useEffect(() => {
    if (!isOpen || childCount < 2) return;
    const measure = () => {
      const row = childrenRowRef.current;
      const first = childRefs.current[0];
      const last = childRefs.current[childCount - 1];
      if (!row || !first || !last) return;
      const firstCenter = first.offsetLeft + first.offsetWidth / 2;
      const lastCenter = last.offsetLeft + last.offsetWidth / 2;
      setHLine({
        left: firstCenter,
        right: row.offsetWidth - lastCenter,
      });
    };
    measure();
    const t = setTimeout(measure, 60);
    return () => clearTimeout(t);
  }, [isOpen, childCount, expanded]);

  return (
    <div style={styles.orgNodeWrap}>
      {/* The box */}
      <NodeBox
        node={node}
        isRoot={isRoot}
        canToggle={hasChildren}
        isOpen={isOpen}
        onBoxClick={(e) => onBoxClick && onBoxClick(e, node)}
        onToggleClick={(e) => {
          e.stopPropagation();
          if (hasChildren) onToggle(node.id);
        }}
      />

      {/* Children subtree */}
      {hasChildren && isOpen && (
        <>
          <div style={styles.vLineDown} />
          {childCount > 1 && (
            <div style={styles.hLineWrap}>
              <div
                style={{
                  ...styles.hLine,
                  left: `${hLine.left}px`,
                  right: `${hLine.right}px`,
                }}
              />
            </div>
          )}
          <div
            ref={childrenRowRef}
            style={{
              ...styles.childrenRow,
              justifyContent:
                childCount === 1 ? "center" : "space-around",
            }}
          >
            {node.children.map((child, idx) => (
              <div
                key={child.id}
                ref={(el) => (childRefs.current[idx] = el)}
                style={styles.childCol}
              >
                <div style={styles.vStub} />
                <OrgNode
                  node={child}
                  depth={depth + 1}
                  expanded={expanded}
                  onToggle={onToggle}
                  onBoxClick={onBoxClick}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ===== Node box =====
const NodeBox = ({ node, isRoot, canToggle, isOpen, onBoxClick, onToggleClick }) => {
  const stripeColor = node.is_off
    ? STATUS_COLORS.OFF
    : node.status_key
      ? STATUS_COLORS[node.status_key]
      : "#cbd5e1";

  const badge = CATEGORY_BADGE[node.category];

  const showPower = !node.is_virtual || Math.abs(node.power) > 0.001;
  const clickable = !node.is_virtual;

  // Tint the whole box with the health-status color (skip virtual nodes)
  const statusBg =
    !node.is_virtual && (node.is_off || node.status_key)
      ? STATUS_BG[node.is_off ? "OFF" : node.status_key]
      : null;

  return (
    <div
      style={{
        ...(isRoot ? styles.parentBox : styles.childBox),
        opacity: node.is_stale ? 0.65 : 1,
        cursor: clickable ? "pointer" : "default",
        borderColor: stripeColor,
        ...(statusBg ? { background: statusBg } : {}),
      }}
      onClick={clickable ? onBoxClick : undefined}
      onMouseEnter={(e) => {
        if (clickable) {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow =
            "0 6px 16px rgba(15,23,42,0.12), 0 2px 4px rgba(15,23,42,0.06)";
        }
      }}
      onMouseLeave={(e) => {
        if (clickable) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = isRoot
            ? "0 4px 14px rgba(15,23,42,0.10), 0 2px 4px rgba(15,23,42,0.04)"
            : "0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)";
        }
      }}
    >
      {/* Stripe */}
      <div
        style={
          isRoot
            ? { ...styles.parentTopStripe, background: stripeColor }
            : { ...styles.childLeftStripe, background: stripeColor }
        }
      />

      {/* Header row */}
      <div style={styles.boxHeader}>
        <div style={styles.boxNameWrap}>
          <span
            style={{
              ...styles.boxName,
              fontSize: isRoot ? 14 : 12,
              color: node.is_virtual ? "#64748b" : "#0f172a",
            }}
          >
            {node.name}
          </span>
          {badge && (
            <span
              style={{
                ...styles.catBadge,
                background: badge.bg,
                color: badge.fg,
              }}
            >
              {badge.label}
            </span>
          )}
        </div>
        {node.is_stale && <span style={styles.staleBadge}>offline</span>}
        {node.is_exporting && (
          <span style={styles.exportBadge}>↑ EXPORT</span>
        )}
      </div>

      {/* Power */}
      {showPower && (
        <div style={styles.boxPowerRow}>
          <span
            style={{
              ...styles.boxPower,
              fontSize: isRoot ? 22 : 16,
              color: node.is_exporting
                ? "#10b981"
                : node.is_off
                  ? "#94a3b8"
                  : "#0f172a",
            }}
          >
            {Math.abs(node.power).toFixed(2)}
          </span>
          <span style={styles.boxPowerUnit}>kW</span>
        </div>
      )}

      {/* Footer */}
      <div style={styles.boxFooter}>
        {node.is_off ? (
          <span style={{ ...styles.statusBadge, ...statusPill("OFF") }}>Off</span>
        ) : node.status_key ? (
          <span
            style={{ ...styles.statusBadge, ...statusPill(node.status_key) }}
            title={HEALTH_TOOLTIPS[node.status_key] || node.status_key}
          >
            {`${Number(node.health_percent).toFixed(1)}%`}
          </span>
        ) : node.is_virtual ? null : (
          <span style={{ ...styles.statusBadge, ...statusPill("Unknown") }}>
            –
          </span>
        )}
        {canToggle && (
          <button
            type="button"
            onClick={onToggleClick}
            style={styles.toggleBtn}
            title={`${node.counts.total} total / ${node.counts.online} online / ${node.counts.offline} offline`}
          >
            <span>{isOpen ? "▾" : "▸"}</span>
            <span style={{ color: "#0f172a" }}>{node.counts.total}</span>
            <span style={{ color: "#94a3b8" }}>/</span>
            <span style={{ color: "#0ea5e9" }}>{node.counts.online}</span>
            <span style={{ color: "#94a3b8" }}>/</span>
            <span style={{ color: "#94a3b8" }}>{node.counts.offline}</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ===== Details Panel (side popup, ported from old LiveTab diagram) =====
const DetailsPanel = ({ node, pos, totalPower, onClose }) => {
  const reading = node.reading;
  const phases = reading?.phases || null;
  const phaseType = reading?.phase_type || null;

  const headerColor =
    node.category === "GRID"
      ? "#3b82f6"
      : node.category === "SOLAR"
        ? "#f59e0b"
        : node.category === "GENERATOR"
          ? "#8b5cf6"
          : node.status_key === "Normal"
            ? "#22c55e"
            : node.status_key === "Warning"
              ? "#f59e0b"
              : node.status_key === "Critical"
                ? "#ef4444"
                : node.is_off
                  ? "#94a3b8"
                  : "#16a34a";

  const isSource = ["GRID", "SOLAR", "GENERATOR"].includes(node.category);
  const status = node.is_off ? "OFF" : "ON";
  const typeLabel = isSource ? node.category : "LOAD";

  return (
    <div
      style={{
        ...styles.detailsPanel,
        top: pos.top,
        left: pos.left,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ ...styles.detailsHeader, background: headerColor }}>
        <span>{node.name}</span>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: "#cbd5e1",
            fontSize: 20,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>
      <div
        style={{
          padding: 16,
          maxHeight: 520,
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <div style={styles.detailRow}>
          <span>Type:</span>
          <strong style={{ textTransform: "uppercase" }}>{typeLabel}</strong>
        </div>
        <div style={styles.detailRow}>
          <span>Status:</span>
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              background: status === "ON" ? "#dcfce7" : "#fee2e2",
              color: status === "ON" ? "#16a34a" : "#dc2626",
            }}
          >
            {status}
          </span>
        </div>
        {node.health_status && (
          <div style={styles.detailRow}>
            <span>Health:</span>
            <strong
              style={{
                color:
                  node.health_status === "Normal"
                    ? "#16a34a"
                    : node.health_status === "Warning"
                      ? "#f59e0b"
                      : node.health_status === "Critical"
                        ? "#ef4444"
                        : "#64748b",
              }}
            >
              {node.health_status}
            </strong>
          </div>
        )}

        {/* Parameters */}
        <div style={styles.detailSection}>
          <div style={styles.detailSectionTitle}>Parameters</div>
          <div style={styles.detailRow}>
            <span>Power:</span>
            <strong style={{ color: "#1BA08E" }}>
              {Math.abs(node.power).toFixed(2)} kW
            </strong>
          </div>
          <div style={styles.detailRow}>
            <span>Voltage:</span>
            <strong>
              {reading?.voltage != null
                ? reading.voltage.toFixed(2) + " V"
                : "-"}
            </strong>
          </div>
          <div style={styles.detailRow}>
            <span>Current:</span>
            <strong>
              {reading?.current != null
                ? reading.current.toFixed(1) + " A"
                : "-"}
            </strong>
          </div>
          <div style={styles.detailRow}>
            <span>Power Factor:</span>
            <strong>
              {reading?.power_factor != null
                ? reading.power_factor.toFixed(2)
                : "-"}
            </strong>
          </div>
          <div style={styles.detailRow}>
            <span>Frequency:</span>
            <strong>
              {reading?.frequency != null
                ? reading.frequency.toFixed(2) + " Hz"
                : "-"}
            </strong>
          </div>
          <div style={styles.detailRow}>
            <span>THD Voltage:</span>
            <strong>
              {reading?.thd_voltage != null
                ? reading.thd_voltage.toFixed(2) + " %"
                : "-"}
            </strong>
          </div>
          <div style={styles.detailRow}>
            <span>THD Current:</span>
            <strong>
              {reading?.thd_current != null
                ? reading.thd_current.toFixed(2) + " %"
                : "-"}
            </strong>
          </div>
          <div style={styles.detailRow}>
            <span>TAP:</span>
            <strong>
              {reading?.tap != null ? reading.tap.toFixed(2) + " kW" : "-"}
            </strong>
          </div>
          <div style={styles.detailRow}>
            <span>% of Total Load:</span>
            <strong>
              {totalPower > 0
                ? ((Math.abs(node.power) / totalPower) * 100).toFixed(1)
                : 0}
              %
            </strong>
          </div>
        </div>

        {/* Per-Phase */}
        {phases && phaseType === 3 && (
          <div style={styles.detailSection}>
            <div style={styles.detailSectionTitle}>Phase Details</div>
            {["phase1", "phase2", "phase3"].map((pk, i) => {
              const p = phases[pk];
              if (!p) return null;
              return (
                <div
                  key={pk}
                  style={{
                    marginBottom: 8,
                    padding: "6px 8px",
                    background: "#f8fafc",
                    borderRadius: 6,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#334155",
                      marginBottom: 4,
                    }}
                  >
                    Phase {i + 1}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "2px 12px",
                      fontSize: 11,
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Voltage:</span>
                    <strong style={{ color: "#334155" }}>
                      {p.voltage?.toFixed(1)} V
                    </strong>
                    <span style={{ color: "#64748b" }}>Current:</span>
                    <strong style={{ color: "#334155" }}>
                      {p.current?.toFixed(1)} A
                    </strong>
                    <span style={{ color: "#64748b" }}>Power:</span>
                    <strong style={{ color: "#334155" }}>
                      {p.power?.toFixed(2)} kW
                    </strong>
                    <span style={{ color: "#64748b" }}>PF:</span>
                    <strong style={{ color: "#334155" }}>
                      {p.power_factor?.toFixed(3)}
                    </strong>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Timestamp */}
        {reading?.timestamp && (
          <div style={styles.detailSection}>
            <div style={styles.detailRow}>
              <span>Last Updated:</span>
              <strong style={{ fontSize: 11, color: "#64748b" }}>
                {reading.timestamp}
              </strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const statusPill = (key) => {
  switch (key) {
    case "Normal":
      return { background: "#dcfce7", color: "#166534" };
    case "Warning":
      return { background: "#fef3c7", color: "#92400e" };
    case "Critical":
      return { background: "#fee2e2", color: "#991b1b" };
    case "OFF":
      return { background: "#f1f5f9", color: "#64748b" };
    case "Virtual":
      return { background: "#f1f5f9", color: "#475569" };
    default:
      return { background: "#f1f5f9", color: "#94a3b8" };
  }
};

// ===== Styles =====
const styles = {
  container: {
    background: "#f8fafc",
    borderRadius: 14,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    height: "calc(100vh - 90px)",
  },
  empty: {
    textAlign: "center",
    padding: 60,
    color: "#64748b",
    fontSize: 14,
  },

  // Header
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(135deg, #0f766e, #0d9488)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 10,
    marginBottom: 10,
    flexWrap: "wrap",
    gap: 8,
  },
  headerLeftGroup: {
    display: "flex",
    alignItems: "flex-end",
    gap: 28,
    flexWrap: "wrap",
  },
  headerLeft: { display: "flex", flexDirection: "column" },
  headerCost: { display: "flex", flexDirection: "column" },
  headerCostValue: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.1,
    fontVariantNumeric: "tabular-nums",
  },
  headerCostCurrency: { fontSize: 12, opacity: 0.85, fontWeight: 500 },
  headerLabel: {
    fontSize: 10,
    letterSpacing: 0.8,
    opacity: 0.85,
    textTransform: "uppercase",
  },
  headerValue: { fontSize: 22, fontWeight: 700, lineHeight: 1.1 },
  headerUnit: { fontSize: 12, fontWeight: 500, opacity: 0.9 },
  headerSub: { fontSize: 11, opacity: 0.8, marginTop: 2 },
  headerCounts: { display: "flex", gap: 6, flexWrap: "wrap" },
  countChip: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.2)",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 11,
  },
  countDot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block" },
  countLabel: { color: "#fff", opacity: 0.9 },
  countNum: { fontSize: 12, fontWeight: 700 },
  headerActions: { display: "flex", gap: 6 },
  actionBtn: {
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#fff",
    padding: "4px 10px",
    borderRadius: 6,
    fontSize: 11,
    cursor: "pointer",
  },

  // Zoomable pane wrapper
  zoomOuter: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  zoomBar: {
    position: "absolute",
    top: 8,
    right: 12,
    zIndex: 5,
    display: "flex",
    alignItems: "center",
    gap: 4,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 999,
    padding: "3px 6px",
    boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
  },
  zoomBtn: {
    width: 26,
    height: 26,
    border: "none",
    borderRadius: 999,
    background: "transparent",
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
  },
  zoomBtnText: { width: "auto", padding: "0 8px", fontSize: 11, fontWeight: 600 },
  zoomPct: {
    fontSize: 11,
    fontWeight: 600,
    color: "#475569",
    minWidth: 36,
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
  },

  // Chart container
  chartWrap: {
    background: "#fff",
    borderRadius: 12,
    padding: "12px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
    cursor: "grab",
    userSelect: "none",
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 0,
  },

  // Org node recursive wrapper
  orgNodeWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "fit-content",
  },

  vLineDown: {
    width: 2,
    height: 18,
    background: "#cbd5e1",
  },
  hLineWrap: {
    position: "relative",
    width: "100%",
    height: 0,
  },
  hLine: {
    position: "absolute",
    top: 0,
    height: 2,
    background: "#cbd5e1",
  },
  childrenRow: {
    display: "flex",
    gap: 10,
    width: "100%",
    flexWrap: "nowrap",
    alignItems: "flex-start",
    position: "relative",
  },
  childCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: "0 0 auto",
  },
  vStub: {
    width: 2,
    height: 14,
    background: "#cbd5e1",
  },

  // Node boxes
  parentBox: {
    background: "#fff",
    borderRadius: 10,
    border: "2px solid",
    padding: "12px 16px 10px",
    minWidth: 200,
    maxWidth: 320,
    boxShadow: "0 4px 14px rgba(15,23,42,0.10), 0 2px 4px rgba(15,23,42,0.04)",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.12s, box-shadow 0.12s",
  },
  parentTopStripe: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  childBox: {
    background: "#fff",
    borderRadius: 9,
    border: "1px solid #e2e8f0",
    padding: "8px 10px 7px 14px",
    minWidth: 160,
    maxWidth: 200,
    boxShadow: "0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.12s, box-shadow 0.12s",
  },
  childLeftStripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
  },

  boxHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    flexWrap: "wrap",
    marginBottom: 5,
  },
  boxNameWrap: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexWrap: "wrap",
  },
  boxName: {
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 150,
  },
  catBadge: {
    fontSize: 9,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 999,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  staleBadge: {
    fontSize: 9,
    background: "#fee2e2",
    color: "#991b1b",
    padding: "1px 6px",
    borderRadius: 999,
    fontWeight: 600,
  },
  exportBadge: {
    fontSize: 9,
    background: "#d1fae5",
    color: "#065f46",
    padding: "1px 6px",
    borderRadius: 999,
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  boxPowerRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    marginBottom: 4,
  },
  boxPower: {
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  boxPowerUnit: { fontSize: 11, color: "#64748b", fontWeight: 500 },

  boxMetrics: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    fontSize: 10,
    color: "#64748b",
    fontVariantNumeric: "tabular-nums",
    marginBottom: 6,
  },
  metric: { display: "inline-flex", gap: 3, alignItems: "center" },
  metricLabel: {
    fontSize: 8,
    color: "#94a3b8",
    fontWeight: 700,
    textTransform: "uppercase",
  },

  boxFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  statusBadge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 7px",
    borderRadius: 999,
  },
  toggleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 999,
    cursor: "pointer",
    lineHeight: 1.2,
    fontVariantNumeric: "tabular-nums",
  },

  // ----- Details Panel -----
  detailsPanel: {
    position: "fixed",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
    width: 300,
    border: "1px solid #e2e8f0",
    zIndex: 1000,
    overscrollBehavior: "contain",
  },
  detailsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: "#1e293b",
    borderRadius: "12px 12px 0 0",
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
    fontSize: 13,
    color: "#64748b",
    gap: 12,
  },
  detailSection: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px solid #e2e8f0",
  },
  detailSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#475569",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};

export default HierarchyTreeView;
